import { CenteredDialog } from '../../components/ui/centered-dialog';
// apps/web/src/pages/pcp/retorno-panel.tsx — Fases 02 e 04 (REM-03, REM-04, OMIE-02..04, D-03..D-05, D-08..D-10).
import { useState, useEffect, type ReactElement } from 'react';
import type { EstadoFaturamentoOmie, FaturamentoRetornoView } from '@pieltec/contracts';
import { apiMutate } from '../../lib/csrf-fetch';
import {
  Loader2,
  CheckCircle2,
  FileText,
  DollarSign,
} from 'lucide-react';

export interface LoteComSaldo {
  readonly id: string;
  readonly loteInternoPieltec: string;
  readonly loteClienteRef: string | null;
  readonly itemId: string;
  readonly saldoDisponivel: number;
}

interface RetornoPanelProps {
  readonly isOpen: boolean;
  readonly clienteId?: string;
  readonly onClose: () => void;
  readonly onRetornoConfirmado: () => void;
}

export const ESTADO_FISCAL_CONFIG: Record<
  EstadoFaturamentoOmie,
  { label: string; descricao: string; corBadge: string; corTexto: string }
> = {
  aguardando_fila: {
    label: 'Aguardando Fila',
    descricao: 'Aguardando fila de processamento',
    corBadge: 'bg-amber-100 text-amber-800 border-amber-300',
    corTexto: 'text-amber-700',
  },
  emitindo_nfe: {
    label: 'Emitindo NF-e',
    descricao: 'Emitindo NF-e de Retorno no Omie',
    corBadge: 'bg-blue-100 text-blue-800 border-blue-300',
    corTexto: 'text-blue-700',
  },
  lancando_titulo: {
    label: 'Lançando Título',
    descricao: 'Lançando título a receber no Omie',
    corBadge: 'bg-indigo-100 text-indigo-800 border-indigo-300',
    corTexto: 'text-indigo-700',
  },
  concluido: {
    label: 'Concluído',
    descricao: 'Faturamento e integração fiscal concluídos',
    corBadge: 'bg-emerald-100 text-emerald-800 border-emerald-300',
    corTexto: 'text-emerald-700',
  },
  acao_necessaria: {
    label: 'Ação Necessária',
    descricao: 'Ação necessária na integração fiscal',
    corBadge: 'bg-red-100 text-red-800 border-red-300',
    corTexto: 'text-red-700',
  },
};

/** D-24: explica o diagnóstico RASCUNHO_FISCAL_PENDENTE; o reenvio existente retoma depois de completar os dados. */
export function mensagemRascunhoPendente(faltas: readonly string[] | undefined): string {
  const lista = faltas && faltas.length > 0 ? faltas.join('; ') : 'dados do rascunho fiscal';
  return `Dados fiscais incompletos — falta: ${lista}. Abra o pedido de venda deste retorno e revise o rascunho fiscal e os valores dos itens. Se a pendência for de modelo fiscal, solicite a revisão ao responsável fiscal antes do reenvio.`;
}

export function FaturamentoFiscalCard({
  retornoId,
  onConcluido,
}: {
  readonly retornoId: string;
  readonly onConcluido?: () => void;
}): ReactElement {
  const [faturamento, setFaturamento] = useState<FaturamentoRetornoView | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [fetchError, setFetchError] = useState<string | null>(null);

  useEffect(() => {
    let timer: NodeJS.Timeout | null = null;
    let isMounted = true;

    const fetchStatus = async () => {
      try {
        const res = await fetch(`/api/v1/omie/faturamentos/retornos/${encodeURIComponent(retornoId)}`);
        if (!res.ok) {
          if (res.status === 404) return;
          throw new Error('Falha ao consultar estado fiscal');
        }
        const data = (await res.json()) as FaturamentoRetornoView;
        if (!isMounted) return;

        setFaturamento(data);
        setFetchError(null);

        if (data.estado === 'concluido' && onConcluido) {
          onConcluido();
        }

        // Continua polling a cada 3s enquanto não for terminal (D-05)
        if (data.estado !== 'concluido' && data.estado !== 'acao_necessaria') {
          timer = setTimeout(() => {
            void fetchStatus();
          }, 3000);
        }
      } catch (err) {
        if (!isMounted) return;
        setFetchError(err instanceof Error ? err.message : 'Erro ao consultar faturamento');
      } finally {
        if (isMounted) setLoading(false);
      }
    };

    void fetchStatus();

    return () => {
      isMounted = false;
      if (timer) clearTimeout(timer);
    };
  }, [retornoId, onConcluido]);

  if (loading && !faturamento) {
    return (
      <div className="p-4 bg-slate-50 border border-slate-200 rounded-lg flex items-center gap-3 text-sm text-slate-600">
        <Loader2 className="w-4 h-4 animate-spin text-blue-600" />
        <span>Consultando estado da integração fiscal Omie...</span>
      </div>
    );
  }

  if (fetchError && !faturamento) {
    return (
      <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-800">
        {fetchError} (a confirmação física permanece válida no estoque).
      </div>
    );
  }

  if (!faturamento) return <></>;

  const config = ESTADO_FISCAL_CONFIG[faturamento.estado] ?? ESTADO_FISCAL_CONFIG.aguardando_fila;
  const isTerminal = faturamento.estado === 'concluido' || faturamento.estado === 'acao_necessaria';

  return (
    <div className="p-4 bg-white border border-slate-200 rounded-lg shadow-sm space-y-3">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <span className="font-semibold text-sm text-slate-900">Integração Fiscal Omie (D-05)</span>
          {!isTerminal && <Loader2 className="w-3.5 h-3.5 animate-spin text-blue-600" />}
        </div>
        <span
          className={`px-2.5 py-1 text-xs font-bold rounded-full border ${config.corBadge}`}
        >
          {config.label}
        </span>
      </div>

      <p className={`text-xs font-medium ${config.corTexto}`}>{config.descricao}</p>

      {/* Documentos emitidos */}
      <div className="grid grid-cols-2 gap-3 pt-1 border-t border-slate-100 text-xs">
        <div className="p-2 bg-slate-50 rounded flex items-center gap-2">
          <FileText className="w-4 h-4 text-slate-500 shrink-0" />
          <div className="truncate">
            <div className="text-slate-500 font-medium">NF-e de Retorno</div>
            <div className="font-mono font-bold text-slate-900 truncate">
              {faturamento.notaFiscalNumero || 'Pendente de emissão'}
            </div>
          </div>
        </div>

        <div className="p-2 bg-slate-50 rounded flex items-center gap-2">
          <DollarSign className="w-4 h-4 text-slate-500 shrink-0" />
          <div className="truncate">
            <div className="text-slate-500 font-medium">Título a Receber</div>
            <div className="font-mono font-bold text-slate-900 truncate">
              {faturamento.tituloNumero || 'Pendente de lançamento'}
            </div>
          </div>
        </div>
      </div>

      {faturamento.diagnosticoCodigo && (
        <div className="text-[11px] font-mono text-red-600 bg-red-50 p-2 rounded border border-red-100">
          Diagnóstico: {faturamento.diagnosticoCodigo}
        </div>
      )}

      {faturamento.diagnosticoCodigo === 'RASCUNHO_FISCAL_PENDENTE' && (
        <p className="text-xs text-red-700" role="alert">
          {mensagemRascunhoPendente(faturamento.faltas)}
        </p>
      )}

      {faturamento.diagnosticoCodigo === 'RASCUNHO_FISCAL_NAO_APROVADO' && (
        <p className="text-xs text-red-700" role="alert">
          Rascunho fiscal ainda não aprovado no PV.
        </p>
      )}
    </div>
  );
}

// L-52 (17.1-17): a saída física do PV é feita só pela Expedição (/expedicao); este painel não oferece esse caminho.
export function RetornoPanel({
  isOpen,
  clienteId,
  onClose,
  onRetornoConfirmado,
}: RetornoPanelProps): ReactElement | null {
  const [lotes, setLotes] = useState<LoteComSaldo[]>([]);
  const [quantidades, setQuantidades] = useState<Record<string, number>>({});
  const [notaFiscalReferencia, setNotaFiscalReferencia] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(false);
  const [submitting, setSubmitting] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [confirmDialogOpen, setConfirmDialogOpen] = useState<boolean>(false);
  const [retornoConfirmadoId, setRetornoConfirmadoId] = useState<string | null>(null);

  useEffect(() => {
    if (!isOpen) {
      setRetornoConfirmadoId(null);
      return;
    }

    const fetchLotes = async () => {
      setLoading(true);
      setError(null);
      try {
        const url = clienteId
          ? `/api/v1/remessas/lotes-disponiveis?clienteId=${encodeURIComponent(clienteId)}`
          : '/api/v1/remessas/lotes-disponiveis';
        const res = await fetch(url);
        if (!res.ok) {
          throw new Error('Falha ao carregar lotes disponíveis para retorno.');
        }
        const data = (await res.json()) as LoteComSaldo[];
        setLotes(data);
        setQuantidades({});
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Erro ao buscar lotes disponíveis');
      } finally {
        setLoading(false);
      }
    };

    void fetchLotes();
  }, [isOpen, clienteId]);

  if (!isOpen) return null;

  const handleQtdChange = (loteId: string, value: string) => {
    const parsed = value === '' ? 0 : parseInt(value, 10);
    setQuantidades((prev) => ({
      ...prev,
      [loteId]: isNaN(parsed) ? 0 : Math.max(0, parsed),
    }));
  };

  // Linhas selecionadas para retorno (quantidade > 0)
  const linhasSelecionadas = lotes
    .filter((l) => (quantidades[l.id] ?? 0) > 0)
    .map((l) => ({
      loteId: l.id,
      loteInternoPieltec: l.loteInternoPieltec,
      saldo: l.saldoDisponivel,
      quantidade: quantidades[l.id] ?? 0,
      invalido: (quantidades[l.id] ?? 0) > l.saldoDisponivel,
    }));

  const totalQuantidade = linhasSelecionadas.reduce((acc, cur) => acc + cur.quantidade, 0);
  const hasExceeded = linhasSelecionadas.some((l) => l.invalido);
  const canConfirm = linhasSelecionadas.length > 0 && !hasExceeded && !submitting;

  const handleSubmit = async () => {
    setSubmitting(true);
    setError(null);
    try {
      const res = await apiMutate('/api/v1/remessas/retorno', {
        method: 'POST',
        body: {
          clienteId: clienteId || 'CLI-GERAL',
          notaFiscalReferencia: notaFiscalReferencia.trim() || undefined,
          linhas: linhasSelecionadas.map((l) => ({ loteId: l.loteId, quantidade: l.quantidade })),
        },
      });

      if (!res.ok) {
        const errJson = (await res.json().catch(() => ({}))) as { message?: string };
        throw new Error(errJson.message || 'Falha ao confirmar retorno de material.');
      }

      const retorno = (await res.json()) as { id: string };
      setRetornoConfirmadoId(retorno.id);
      setConfirmDialogOpen(false);
      onRetornoConfirmado();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erro ao submeter retorno');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <CenteredDialog title="Retorno de Material" onClose={onClose}>
      <div className="bg-white rounded-lg shadow-xl border border-slate-200 w-full max-w-3xl flex flex-col max-h-[90vh]">
        {/* Header */}
        <div className="p-6 border-b border-slate-200 flex items-center justify-between">
          <div>
            <h2 className="text-xl font-bold text-slate-900">Retorno de Material</h2>
            <p className="text-sm text-slate-500 mt-1">
              Selecione os lotes e informe as quantidades para devolução ao cliente
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="text-slate-400 hover:text-slate-600 text-lg font-bold p-2"
          >
            ✕
          </button>
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto flex-1 space-y-4">
          {error && (
            <div className="p-3 bg-red-50 border border-red-200 text-red-700 text-sm rounded-md" role="alert">
              {error}
            </div>
          )}

          {retornoConfirmadoId ? (
            <div className="space-y-4">
              <div className="p-4 bg-emerald-50 border border-emerald-200 rounded-lg text-emerald-800 flex items-center gap-3">
                <CheckCircle2 className="w-5 h-5 text-emerald-600 shrink-0" />
                <div>
                  <div className="font-bold text-sm">Retorno físico confirmado no estoque!</div>
                  <div className="text-xs text-emerald-700 mt-0.5">
                    Saída gravada no ledger com sucesso. Acompanhe a integração fiscal abaixo:
                  </div>
                </div>
              </div>

              <FaturamentoFiscalCard retornoId={retornoConfirmadoId} />
            </div>
          ) : (
            <>
              <p className="text-xs text-slate-500" data-testid="retorno-orientacao-lotes-pv">
                Lotes de pedido de venda não aparecem aqui: eles saem pela aba Fechamento do pedido e pela Expedição.
              </p>
              <div>
                <label className="block text-xs font-semibold text-slate-700 uppercase tracking-wider mb-1" htmlFor="field-retorno-panel-12519">
                  Referência da NF-e de retorno (opcional)
                </label>
                <input id="field-retorno-panel-12519"
                  type="text"
                  value={notaFiscalReferencia}
                  onChange={(e) => setNotaFiscalReferencia(e.target.value)}
                  placeholder="Ex: NF-RET-2026-99"
                  className="w-full px-3 py-2 border border-slate-300 rounded-md text-sm focus:ring-2 focus:ring-blue-500 focus:outline-none"
                />
              </div>

              {loading ? (
                <div className="py-8 text-center text-sm text-slate-500">Carregando lotes com saldo disponível...</div>
              ) : lotes.length === 0 ? (
                <div className="py-8 text-center text-sm text-slate-500">
                  Nenhum lote com saldo disponível para retorno.
                </div>
              ) : (
                <div className="border border-slate-200 rounded-md overflow-hidden">
                  <table className="w-full text-left text-sm">
                    <thead className="bg-slate-50 text-slate-700 font-semibold border-b border-slate-200 text-xs">
                      <tr>
                        <th className="p-3">Lote Interno</th>
                        <th className="p-3">Item</th>
                        <th className="p-3">Lote do Cliente</th>
                        <th className="p-3 text-right">Saldo Disponível</th>
                        <th className="p-3 w-36 text-right">Qtd Retorno</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                      {lotes.map((lote) => {
                        const qtd = quantidades[lote.id] ?? 0;
                        const invalido = qtd > lote.saldoDisponivel;
                        return (
                          <tr key={lote.id} className="hover:bg-slate-50">
                            <td className="p-3 font-mono font-medium text-slate-900">{lote.loteInternoPieltec}</td>
                            <td className="p-3 text-slate-700">{lote.itemId}</td>
                            <td className="p-3 text-slate-500">{lote.loteClienteRef || '-'}</td>
                            <td className="p-3 text-right font-mono font-medium text-slate-800">
                              {lote.saldoDisponivel}
                            </td>
                            <td className="p-3 text-right">
                              <input
                                type="number"
                                min="0"
                                value={quantidades[lote.id] === undefined || quantidades[lote.id] === 0 ? '' : quantidades[lote.id]}
                                onChange={(e) => handleQtdChange(lote.id, e.target.value)}
                                placeholder="0"
                                className={`w-28 text-right font-mono px-2 py-1 border rounded text-sm focus:outline-none focus:ring-2 ${
                                  invalido
                                    ? 'border-red-500 text-red-700 bg-red-50 focus:ring-red-500'
                                    : 'border-slate-300 focus:ring-blue-500'
                                }`}
                              />
                              {invalido && (
                                <div className="text-xs text-red-600 mt-1 text-right">
                                  Quantidade informada ({qtd}) excede o saldo disponível ({lote.saldoDisponivel}) do lote{' '}
                                  {lote.loteInternoPieltec}.
                                </div>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </>
          )}
        </div>

        {/* Footer */}
        <div className="p-6 border-t border-slate-200 bg-slate-50 flex items-center justify-between">
          <div className="text-sm font-semibold text-slate-700">
            Total a retornar: <span className="font-mono text-slate-900 text-base">{totalQuantidade} un</span>
          </div>
          <div className="flex gap-3">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 border border-slate-300 text-slate-700 text-sm font-medium rounded hover:bg-slate-100"
            >
              {retornoConfirmadoId ? 'Fechar' : 'Cancelar'}
            </button>
            {!retornoConfirmadoId && (
              <button
                type="button"
                disabled={!canConfirm}
                onClick={() => setConfirmDialogOpen(true)}
                className="px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Confirmar retorno
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Confirmation Dialog */}
      {confirmDialogOpen && (
        <div className="fixed inset-0 z-60 flex items-center justify-center bg-slate-900/60 p-4">
          <div className="bg-white rounded-lg shadow-xl border border-slate-200 p-6 max-w-md w-full space-y-4">
            <h3 className="text-lg font-bold text-slate-900">Resumo do Retorno</h3>
            <p className="text-sm text-slate-600">
              Confira os lotes e quantidades que serão baixados no ledger de estoque:
            </p>
            <div className="border border-slate-200 rounded p-3 max-h-48 overflow-y-auto divide-y divide-slate-100">
              {linhasSelecionadas.map((l) => (
                <div key={l.loteId} className="py-1.5 flex justify-between text-sm">
                  <span className="font-mono font-medium text-slate-800">{l.loteInternoPieltec}</span>
                  <span className="font-mono text-slate-900 font-semibold">{l.quantidade} un</span>
                </div>
              ))}
            </div>
            <div className="flex justify-end gap-3 pt-2">
              <button
                type="button"
                disabled={submitting}
                onClick={() => setConfirmDialogOpen(false)}
                className="px-3 py-1.5 border border-slate-300 text-slate-700 text-sm rounded"
              >
                Voltar e ajustar
              </button>
              <button
                type="button"
                disabled={submitting}
                onClick={() => void handleSubmit()}
                className="px-4 py-1.5 bg-blue-600 text-white text-sm font-medium rounded hover:bg-blue-700 disabled:opacity-50"
              >
                {submitting ? 'Confirmando...' : 'Confirmar e baixar saldo'}
              </button>
            </div>
          </div>
        </div>
      )}
    </CenteredDialog>
  );
}
